import axios from 'axios';
import { getBackendBaseUrl } from './backendUrl';

const backendBaseUrl = getBackendBaseUrl();
const API_URL = `${backendBaseUrl}/api`;
const CURRENT_AGREEMENT_VERSION = 10;

export const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
});

let refreshSessionPromise = null;

function createUploadProgressHandler(onProgress) {
  return (event) => {
    if (!onProgress) return;
    const loaded = Number(event?.loaded || 0);
    const total = Number(event?.total || 0);
    if (loaded <= 0 || total <= 0) return;
    onProgress(Math.max(0, Math.min(100, Math.round((loaded * 100) / total))));
  };
}

async function refreshSession() {
  if (!refreshSessionPromise) {
    refreshSessionPromise = api.post('/auth/refresh', null, {
      skipAuthRefresh: true,
      timeout: 15000,
    }).finally(() => {
      refreshSessionPromise = null;
    });
  }
  return refreshSessionPromise;
}

const AGREEMENT_ALLOWLIST = [
  '/auth/',
  '/user/accept-agreement',
  '/help/articles',
  '/public/site-status',
  '/public/cmg',
  '/public/ourdesigns',
  '/public/legal-pages',
  '/public/artist-awards',
  '/social/report-reasons',
  '/social/dmca-notices',
];

function isHtmlDocument(value) {
  return typeof value === 'string'
    && /<!doctype html|<html(?:\s|>)/i.test(value);
}

function normalizeHtmlErrorResponse(error) {
  const data = error?.response?.data;
  const detail = data?.detail ?? data;
  const raw = data?.body?.raw || data?.error?.body?.raw || data?.detail?.body?.raw;
  if (!isHtmlDocument(detail) && !isHtmlDocument(raw)) {
    return;
  }
  const structuredDetail = data?.detail && typeof data.detail === 'object' ? data.detail : null;
  const upstreamMessage = structuredDetail?.message || structuredDetail?.body?.error || structuredDetail?.body?.message;
  const upstreamContext = structuredDetail?.method && structuredDetail?.path
    ? `${structuredDetail.method} ${structuredDetail.path}`
    : '';
  error.response.data = {
    detail: {
      message: upstreamMessage || 'The API returned the TRB maintenance page instead of JSON.',
      code: 'api_maintenance_html',
      status_code: error.response.status,
      upstream_status_code: structuredDetail?.status_code,
      upstream_context: upstreamContext || undefined,
      next_step: upstreamMessage
        ? 'Check the upstream service/config named in this error, then retry the action.'
        : 'Check PM2/nginx and backend.env, then retry the action.',
    },
  };
}

function getCachedAuthUser() {
  try {
    const raw = localStorage.getItem('auth_user');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function isAgreementGateLocked() {
  const user = getCachedAuthUser();
  if (!user) return false;
  const version = Number(user.agreement_version_accepted || 0);
  return Number.isFinite(version) ? version < CURRENT_AGREEMENT_VERSION : true;
}

function isAllowedDuringAgreementGate(config) {
  const url = (config?.url || '').toString();
  return AGREEMENT_ALLOWLIST.some((prefix) => url.startsWith(prefix));
}

api.interceptors.request.use((config) => {
  if (isAgreementGateLocked() && !isAllowedDuringAgreementGate(config)) {
    const error = new Error('Agreement acceptance required');
    error.code = 'ERR_AGREEMENT_GATE';
    error.isAgreementGate = true;
    return Promise.reject(error);
  }

  const method = String(config?.method || 'get').toLowerCase();
  if (method === 'get' && typeof config.timeout !== 'number' && config.responseType !== 'blob') {
    config.timeout = 15000;
  }

  return config;
});

api.interceptors.response.use(
  (response) => {
    const contentType = String(response?.headers?.['content-type'] || '').toLowerCase();
    if (!contentType.includes('text/html') && !isHtmlDocument(response?.data)) {
      return response;
    }
    const error = new Error('The API returned HTML instead of JSON.');
    error.config = response.config;
    error.response = {
      ...response,
      data: {
        detail: {
          message: 'The API returned HTML instead of JSON; the action was not marked successful.',
          code: 'unexpected_api_html',
          status_code: response.status,
          next_step: 'Check the active nginx API configuration and backend logs before retrying.',
        },
      },
    };
    return Promise.reject(error);
  },
  async (error) => {
    const originalConfig = error.config || {};
    normalizeHtmlErrorResponse(error);
    const status = error.response?.status;
    const detail = error.response?.data?.detail;

    if (status === 403 && detail?.code === 'account_banned' && typeof window !== 'undefined') {
      try {
        window.sessionStorage.setItem('account-ban-status', JSON.stringify(detail));
      } catch {
        // Ignore storage failures; the status page still renders a generic state.
      }
      if (!window.location.pathname.includes('account-ban-status') && !window.location.pathname.includes('accountbanstatus')) {
        window.location.replace('/account-ban-status');
      }
    }

    if (error.response?.status === 503 && error.response?.data?.code === 'maintenance' && typeof window !== 'undefined') {
      const nextUrl = '/';
      if (window.location.pathname !== nextUrl) {
        window.location.replace(nextUrl);
      } else {
        window.location.reload();
      }
    }

    const requestUrl = (originalConfig.url || '').toString();
    const shouldTryRefresh =
      status === 401 &&
      !originalConfig._retryAfterRefresh &&
      !originalConfig.skipAuthRefresh &&
      !requestUrl.startsWith('/auth/login') &&
      !requestUrl.startsWith('/auth/register') &&
      !requestUrl.startsWith('/auth/refresh') &&
      !requestUrl.startsWith('/auth/logout');

    if (shouldTryRefresh) {
      try {
        await refreshSession();
        originalConfig._retryAfterRefresh = true;
        return api(originalConfig);
      } catch (refreshError) {
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

export default api;

// Auth
export const authAPI = {
  login: (data) => api.post('/auth/login', data),
  register: (data) => api.post('/auth/register', data),
  registerAgeCheckStatus: (data) => api.post('/auth/register/age-check-status', data),
  completeRegisterAgeCheck: (data) => api.post('/auth/register/complete-age-check', data),
  logout: () => api.post('/auth/logout'),
  stopImpersonation: () => api.post('/auth/impersonation/stop'),
  me: () => api.get('/auth/me'),
  updateProfile: (data) => api.patch('/auth/profile', data),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, password) => api.post('/auth/reset-password', { token, password }),
  changePassword: (currentPassword, newPassword) => api.post('/auth/change-password', {
    current_password: currentPassword,
    new_password: newPassword,
  }),
  createSupportToken: () => api.post('/account/support-token'),
  revokeSupportToken: () => api.post('/account/support-token/revoke'),
};

export const userAPI = {
  acceptAgreement: () => api.post('/user/accept-agreement'),
  getAccountStanding: () => api.get('/account/standing'),
  createRightsAppeal: (data) => api.post('/account/rights-appeals', data),
};

export const securityAPI = {
  getCenter: () => api.get('/account/security'),
  setupMfa: (password) => api.post('/account/security/mfa/setup', { password }),
  enableMfa: (code) => api.post('/account/security/mfa/enable', { code }),
  disableMfa: (password, code) => api.post('/account/security/mfa/disable', { password, code }),
  updateLoginAlerts: (loginAlertsEnabled) => api.patch('/account/security/alerts', { login_alerts_enabled: loginAlertsEnabled }),
  revokeSession: (sessionId) => api.delete(`/account/security/sessions/${encodeURIComponent(sessionId)}`),
  signOutOtherSessions: () => api.post('/account/security/sessions/sign-out-others'),
};

export const privacyAPI = {
  getCenter: () => api.get('/account/privacy'),
  updatePreferences: (preferences) => api.patch('/account/privacy/preferences', preferences),
  exportData: () => api.get('/account/privacy/export', { responseType: 'blob' }),
  requestDeletion: (payload) => api.post('/account/privacy/deletion-request', payload),
  cancelDeletion: () => api.post('/account/privacy/deletion-request/cancel'),
};

export const socialAPI = {
  getFeed: (params = {}) => api.get('/social/feed', { params }),
  search: (params = {}) => api.get('/social/search', { params }),
  getSuggestions: (params = {}) => api.get('/social/suggestions', { params }),
  getUserProfile: (id, params = {}) => api.get(`/social/users/${id}`, { params }),
  getReportReasons: () => api.get('/social/report-reasons'),
  getMyProfile: () => api.get('/social/me/profile'),
  updateMyProfile: (data) => api.patch('/social/me/profile', data),
  uploadProfileImage: (file, kind = 'profile') => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/social/me/upload-image', formData, {
      params: { kind },
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getInstants: (params = {}) => api.get('/social/instants', { params }),
  uploadInstantVideo: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/social/me/upload-instant-video', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  uploadStoryMedia: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/social/me/upload-story-media', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getMyStatus: () => api.get('/social/me/status'),
  getMyAccess: () => api.get('/social/me/access'),
  getNotifications: (params = {}) => api.get('/social/notifications', { params }),
  markNotificationsRead: () => api.post('/social/notifications/read-all'),
  getSafetyLists: () => api.get('/social/me/safety'),
  acknowledgeWarning: () => api.post('/social/me/warnings/acknowledge'),
  exportMine: () => api.get('/social/me/export'),
  getBookmarks: (params = {}) => api.get('/social/bookmarks', { params }),
  createPost: (data) => api.post('/social/posts', data),
  updatePost: (id, data) => api.patch(`/social/posts/${id}`, data),
  deletePost: (id) => api.delete(`/social/posts/${id}`),
  getPost: (id) => api.get(`/social/posts/${id}`),
  createComment: (id, data) => api.post(`/social/posts/${id}/comments`, data),
  updateComment: (id, data) => api.patch(`/social/comments/${id}`, data),
  deleteComment: (id) => api.delete(`/social/comments/${id}`),
  likePost: (id) => api.post(`/social/posts/${id}/like`),
  unlikePost: (id) => api.delete(`/social/posts/${id}/like`),
  bookmarkPost: (id) => api.post(`/social/posts/${id}/bookmark`),
  unbookmarkPost: (id) => api.delete(`/social/posts/${id}/bookmark`),
  repostPost: (id) => api.post(`/social/posts/${id}/repost`),
  unrepostPost: (id) => api.delete(`/social/posts/${id}/repost`),
  reportPost: (id, data) => api.post(`/social/posts/${id}/report`, data),
  createAppeal: (data) => api.post('/social/appeals', data),
  createDmcaNotice: (data) => api.post('/social/dmca-notices', data),
  followArtist: (id) => api.post(`/social/artists/${id}/follow`),
  unfollowArtist: (id) => api.delete(`/social/artists/${id}/follow`),
  followUser: (id) => api.post(`/social/users/${id}/follow`),
  unfollowUser: (id) => api.delete(`/social/users/${id}/follow`),
  blockUser: (id) => api.post(`/social/users/${id}/block`),
  unblockUser: (id) => api.delete(`/social/users/${id}/block`),
  muteUser: (id) => api.post(`/social/users/${id}/mute`),
  unmuteUser: (id) => api.delete(`/social/users/${id}/mute`),
};

export const verificationAPI = {
  getPlans: () => api.get('/verification/plans'),
  getMine: () => api.get('/verification/me'),
  apply: (data) => api.post('/verification/apply', data),
  resumePaymentSetup: (applicationId, data) => api.post(`/verification/applications/${applicationId}/payment-setup`, data),
  subscribe: (data) => api.post('/verification/subscribe', data),
  cancelSubscription: (data = {}) => api.post('/verification/subscription/cancel', data),
  changeSubscription: (data) => api.post('/verification/subscription/change', data),
};

export const artistProfileAPI = {
  getMine: () => api.get('/artist-profile/me'),
  updateMine: (data) => api.patch('/artist-profile/me', data),
  requestVerification: (data) => api.post('/artist-profile/me/request-verification', data),
  uploadImage: (kind, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/artist-profile/me/upload-image', formData, {
      params: { kind },
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

export const signupKycAPI = {
  start: () => api.post('/kyc/signup/start'),
  status: () => api.get('/kyc/signup/status'),
};

export const complianceAPI = {
  upload: (file, formType) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('form_type', formType);
    return api.post('/compliance/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Onboarding
export const onboardingAPI = {
  setPlan: (plan) => api.post('/onboarding/plan', { plan }),
  setProfile: (data) => api.post('/onboarding/profile', data),
};

// Partner
export const partnerAPI = {
  apply: (data) => api.post('/partner/apply', data),
};

export const developerAPI = {
  apply: (data) => api.post('/developer/apply', data),
  getAccount: () => api.get('/developer/me'),
  createKey: (data) => api.post('/developer/keys', data),
  revokeKey: (keyId, data = {}) => api.post(`/developer/keys/${keyId}/revoke`, data),
};

export const apiPortalAPI = {
  getSummary: () => api.get('/api-portal/summary'),
  createKey: (data) => api.post('/developer/keys', data),
  revokeKey: (keyId, data = {}) => api.post(`/developer/keys/${keyId}/revoke`, data),
  getDeliveryWebhooks: () => api.get('/developer/delivery-webhooks'),
  createDeliveryWebhook: (data) => api.post('/developer/delivery-webhooks', data),
  deleteDeliveryWebhook: (id) => api.delete(`/developer/delivery-webhooks/${id}`),
};

export const planInquiryAPI = {
  create: (data) => api.post('/plans/inquiries', data),
  getAll: () => api.get('/plans/inquiries'),
};

export const trbMusicGroupAPI = {
  submit: (data) => api.post('/public/trb-music-group/submissions', data),
};

export const publicAPI = {
  submitTakedownRequest: (data) => api.post('/public/takedown-request', data),
  preSave: (releaseId, data) => api.post(`/public/releases/${releaseId}/pre-save`, data),
  startSpotifyPreSave: (releaseId, data) => api.post(`/public/releases/${releaseId}/spotify-pre-save`, data),
  getPreSaveRelease: (releaseId) => api.get(`/public/releases/${releaseId}/pre-save`),
  getSiteStatus: () => api.get('/public/site-status', { params: { _ts: Date.now() } }),
  checkMusicNoteBadge: (youtubeUrl) => api.post('/public/music-note-badge-check', { youtube_url: youtubeUrl }),
  getHelpArticles: () => api.get('/help/articles'),
  getSiteAnnouncement: () => api.get('/public/site-announcement'),
  getLegalPage: (slug) => api.get(`/public/legal-pages/${slug}`),
  getSiteStaff: () => api.get('/public/site-staff'),
  getLabelSite: (slug) => api.get(`/public/label-sites/${slug}`),
  getLabelSiteByHost: (hostname) => api.get(`/public/label-sites/by-host/${encodeURIComponent(hostname)}`),
  getDistroGridPortalByHost: (hostname) => api.get(`/distrogrid/public/portal-context/${encodeURIComponent(hostname)}`),
  getDistroGridPortalBySlug: (slug) => api.get(`/distrogrid/public/portal-context/by-slug/${encodeURIComponent(slug)}`),
  getCMGSite: () => api.get('/public/cmg'),
  getOurDesignsSite: () => api.get('/public/ourdesigns'),
  submitOurDesignsInquiry: (data) => api.post('/public/ourdesigns/inquiries', data),
  getArtistSite: (slug) => api.get(`/public/artist-sites/${slug}`),
  getAffiliatePage: (slug) => api.get(`/public/affiliates/${slug}`),
  trackAffiliateClick: (slug) => api.post(`/public/affiliates/${slug}/click`),
  getGiveawayStatus: () => api.get('/public/giveaway/status', { params: { _ts: Date.now() } }),
  trackGiveawayEvent: (data) => api.post('/public/giveaway/track', data),
  getNewsPosts: () => api.get('/public/news'),
  getNewsPost: (slug) => api.get(`/public/news/${slug}`),
  getRoadmapItems: () => api.get('/public/roadmap'),
  getArtistProfile: (artistRef) => api.get(`/public/artists/${artistRef}`),
  subscribeToArtist: (artistRef, data) => api.post(`/public/artists/${artistRef}/subscribe`, data),
  getStoreReleases: (params = {}) => api.get('/public/store/releases', { params }),
  getStoreRelease: (id) => api.get(`/public/store/releases/${id}`),
  recordStoreStreamEvent: (trackId, data) => api.post(`/public/store/tracks/${encodeURIComponent(trackId)}/stream-event`, data),
  getArtistAwardSummary: () => api.get('/public/artist-awards/summary'),
  getArtistAwardWall: (limit) => api.get('/public/artist-awards/wall', { params: limit ? { limit } : {} }),
  getFrontPageArtistAwards: (limit) => api.get('/public/artist-awards/front-page', { params: limit ? { limit } : {} }),
  getArtistAward: (awardId) => api.get(`/public/artist-awards/${awardId}`),
  getMafa: () => api.get('/public/mfeb'),
  submitMafaApplication: (data) => api.post('/public/mfeb/apply', data),
  getBookingCalendar: () => api.get('/public/calendar'),
  getBookingAvailability: (date) => api.get('/public/calendar/availability', { params: { date } }),
  bookCalendar: (data) => api.post('/public/calendar/book', data),
};

export const statusAPI = {
  getSiteStatus: () => api.get('/public/site-status', { params: { _ts: Date.now() } }),
};

export const labelSiteAPI = {
  getMine: () => api.get('/label-site/me'),
  saveMine: (data) => api.put('/label-site/me', data),
  uploadImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/label-site/me/upload-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

export const cmgAPI = {
  getPublic: () => api.get('/public/cmg'),
  getSummary: () => api.get('/cmg/admin/summary'),
  getSite: () => api.get('/cmg/admin/site'),
  saveSite: (data) => api.put('/cmg/admin/site', data),
  uploadImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/cmg/admin/upload-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getLabels: () => api.get('/cmg/admin/labels'),
  createLabel: (data) => api.post('/cmg/admin/labels', data),
  updateLabel: (id, data) => api.patch(`/cmg/admin/labels/${id}`, data),
  reorderLabels: (items) => api.post('/cmg/admin/labels/reorder', { items }),
  getArtists: () => api.get('/cmg/admin/artists'),
  searchPlatformArtists: (q) => api.get('/cmg/admin/platform-artists', { params: { q } }),
  createArtist: (data) => api.post('/cmg/admin/artists', data),
  updateArtist: (id, data) => api.patch(`/cmg/admin/artists/${id}`, data),
  reorderArtists: (items) => api.post('/cmg/admin/artists/reorder', { items }),
  createUploadRelease: (data) => api.post('/cmg/admin/uploads/releases', data),
  getReleases: (q = '') => api.get('/cmg/admin/releases', { params: q ? { q } : undefined }),
  getContentIdSummary: () => api.get('/cmg/admin/content-id'),
  updateRelease: (data) => api.patch('/cmg/admin/releases', data),
  deleteRelease: (releaseId) => api.delete(`/cmg/admin/releases/${releaseId}`),
  reorderReleases: (items) => api.post('/cmg/admin/releases/reorder', { items }),
  getTeam: () => api.get('/cmg/admin/team'),
  inviteTeamMember: (data) => api.post('/cmg/admin/team/invite', data),
  updateTeamMember: (id, data) => api.patch(`/cmg/admin/team/members/${id}`, data),
};

export const ourDesignsAPI = {
  getPublic: () => api.get('/public/ourdesigns'),
  getSite: () => api.get('/ourdesigns/admin/site'),
  saveSite: (data) => api.put('/ourdesigns/admin/site', data),
  uploadImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/ourdesigns/admin/upload-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

export const artistSiteAPI = {
  getMine: () => api.get('/artist-site/me'),
  saveMine: (data) => api.put('/artist-site/me', data),
  uploadImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/artist-site/me/upload-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

export const bioLinksAPI = {
  getMine: () => api.get('/bio-links/me'),
  saveMine: (data) => api.put('/bio-links/me', data),
  getAnalytics: () => api.get('/bio-links/me/analytics'),
  uploadImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/bio-links/me/upload-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

export const careersAPI = {
  getListings: () => api.get('/careers/listings'),
  apply: (data) => {
    const formData = new FormData();
    formData.append('listing_id', data.listing_id);
    formData.append('listing_title', data.listing_title);
    formData.append('full_name', data.full_name);
    formData.append('email', data.email);
    formData.append('cover_letter', data.cover_letter);
    if (data.location) formData.append('location', data.location);
    if (data.portfolio_url) formData.append('portfolio_url', data.portfolio_url);
    if (data.linkedin_url) formData.append('linkedin_url', data.linkedin_url);
    if (data.experience) formData.append('experience', data.experience);
    if (data.availability) formData.append('availability', data.availability);
    if (data.resume_file) formData.append('resume_file', data.resume_file);
    return api.post('/careers/apply', formData);
  },
};

export const paymentsAPI = {
  createCheckout: (originUrl, plan = 'label', referralSlug = '', promoCode = '', applicationId = '') => api.post('/payments/create-checkout', {
    origin_url: originUrl,
    plan,
    referral_slug: referralSlug || undefined,
    promo_code: promoCode || undefined,
    application_id: applicationId || undefined,
  }),
  createSignupCardCheckCheckout: (originUrl, nextPath = '/onboarding/plan') => api.post('/payments/create-signup-card-check-checkout', {
    origin_url: originUrl,
    next_path: nextPath,
  }),
  createVideoCheckout: (originUrl, releaseId, promoCode = '') => api.post('/payments/create-video-checkout', {
    origin_url: originUrl,
    release_id: releaseId,
    promo_code: promoCode || undefined,
  }),
  createPriorityReleaseCheckout: (originUrl, releaseId) => api.post('/payments/create-priority-release-checkout', {
    origin_url: originUrl,
    release_id: releaseId,
  }),
  createReleaseAddonsCheckout: (originUrl, releaseId, addons = {}) => api.post('/payments/create-release-addons-checkout', {
    origin_url: originUrl,
    release_id: releaseId,
    priority_release: Boolean(addons.priority_release),
    cover_song_license: Boolean(addons.cover_song_license),
  }),
  createVevoChannelCheckout: (originUrl, releaseId, promoCode = '') => api.post('/payments/create-vevo-channel-checkout', {
    origin_url: originUrl,
    release_id: releaseId,
    promo_code: promoCode || undefined,
  }),
  createVevoChannelSignupCheckout: (originUrl, promoCode = '') => api.post('/payments/create-vevo-channel-signup-checkout', {
    origin_url: originUrl,
    promo_code: promoCode || undefined,
  }),
  createArtistSiteCheckout: (originUrl, siteSlug = '') => api.post('/payments/create-artist-site-checkout', {
    origin_url: originUrl,
    site_slug: siteSlug || undefined,
  }),
  createMasteringCheckout: (originUrl, purchaseType, jobId = '') => api.post('/payments/create-mastering-checkout', {
    origin_url: originUrl,
    purchase_type: purchaseType,
    job_id: jobId || undefined,
  }),
  checkStatus: (sessionId) => api.get(`/payments/status/${sessionId}`),
};

export const masteringAPI = {
  getDashboard: () => api.get('/mastering'),
  getJob: (jobId) => api.get(`/mastering/jobs/${encodeURIComponent(jobId)}`),
  createJob: (file, intensity = 'medium', eq = 'neutral', onUploadProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('intensity', intensity);
    formData.append('eq', eq);
    return api.post('/mastering/jobs', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
    });
  },
};

export const vevoAPI = {
  getCurrentSignup: () => api.get('/vevo/channel-signups/me'),
  getArtistOptions: () => api.get('/vevo/artist-options'),
  submitSignup: (data) => api.post('/vevo/channel-signups', data),
};

export const billingAPI = {
  getSummary: () => api.get('/billing/summary'),
  createCaseCheckout: (caseId, originUrl) => api.post(`/account/cases/${caseId}/checkout`, { origin_url: originUrl }),
  cancelMySubscription: (reason) => api.post('/billing/cancel-my-subscription', {
    reason: reason || undefined,
  }),
  changeMySubscription: (plan) => api.post('/billing/change-my-subscription', {
    plan,
  }),
  dismissTrialOffer: () => api.post('/billing/trial-offer/dismiss'),
};

export const notificationsAPI = {
  getAll: (limit = 25) => api.get('/notifications', { params: { limit } }),
  markRead: (id, isRead = true) => api.post(`/notifications/${id}/read`, { is_read: isRead }),
  markAllRead: () => api.post('/notifications/read-all'),
};

// Releases
export const releasesAPI = {
  getDeliveryStores: () => api.get('/delivery-stores'),
  getMyCollaboratorSplits: (status) => api.get('/collaborator-splits/mine', { params: { status } }),
  create: (data) => api.post('/releases', data),
  createCatalogMigration: (data) => api.post('/catalog-migrations', data),
  searchSpotifyTransferCatalog: (params) => api.get('/catalog-migrations/spotify-search', { params }),
  getAll: (status, ownedOnly = true, limit) => api.get('/releases', { params: { status, owned_only: ownedOnly, limit } }),
  getOne: (id) => api.get(`/releases/${id}`),
  getPublicOne: (id) => api.get(`/public/releases/${id}`),
  getArtistLibrary: () => api.get('/artists/library'),
  getVerificationRequest: (id) => api.get(`/releases/${id}/verification-request`),
  getCommandCenter: (id) => api.get(`/releases/${id}/command-center`),
  getCampaignKit: (id) => api.get(`/releases/${id}/campaign-kit`),
  refreshSmartLinks: (id, sourceUrl = '') => api.post(`/releases/${id}/smart-links/refresh`, {
    source_url: sourceUrl || undefined,
  }),
  addComment: (id, data) => api.post(`/releases/${id}/comments`, data),
  requestApproval: (id, data) => api.post(`/releases/${id}/approval-requests`, data),
  updateApproval: (id, approvalId, data) => api.patch(`/releases/${id}/approval-requests/${approvalId}`, data),
  respondToCollaboratorSplit: (id, status) => api.post(`/releases/${id}/collaborator-splits/respond`, { status }),
  resendCollaboratorSplit: (id, splitEmail) => api.post(`/releases/${id}/collaborator-splits/${encodeURIComponent(splitEmail)}/resend`),
  submitVerificationResponse: (id, data) => api.post(`/releases/${id}/verification-response`, data),
  update: (id, data) => api.patch(`/releases/${id}`, data),
  submit: (id) => api.post(`/releases/${id}/submit`),
  ownerApprove: (id) => api.post(`/releases/${id}/owner-approve`),
  delete: (id) => api.delete(`/releases/${id}`),
  requestTakedown: (id) => api.post(`/releases/${id}/request-takedown`),
  uploadArtwork: (id, file, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/releases/${id}/artwork`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: createUploadProgressHandler(onProgress),
    });
  },
  uploadMotionArtwork: (id, aspect, file, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/releases/${id}/motion-artwork/${aspect}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: createUploadProgressHandler(onProgress),
    });
  },
  uploadVideo: (id, file, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/releases/${id}/video`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: createUploadProgressHandler(onProgress),
    });
  },
  uploadAgreementDocument: (id, file, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/releases/${id}/agreement-document`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: createUploadProgressHandler(onProgress),
    });
  },
  getSubmissionAttachment: (id) => api.get(`/releases/${id}/submission-attachment`),
  uploadSubmissionAttachment: (id, file, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/releases/${id}/submission-attachment`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: createUploadProgressHandler(onProgress),
    });
  },
  deleteSubmissionAttachment: (id) => api.delete(`/releases/${id}/submission-attachment`),
  uploadAudio: (id, file, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/releases/${id}/audio`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: createUploadProgressHandler(onProgress),
    });
  },
  uploadTrackAudio: (id, trackNumber, file, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/releases/${id}/tracks/${trackNumber}/audio`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: createUploadProgressHandler(onProgress),
    });
  },
  uploadTrackAtmos: (id, trackNumber, file, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/releases/${id}/tracks/${trackNumber}/atmos`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: createUploadProgressHandler(onProgress),
    });
  },
  getTrackInstrumentalAlternate: (id, trackNumber) => api.get(`/releases/${id}/tracks/${trackNumber}/instrumental-alternate`),
  uploadTrackInstrumentalAlternate: (id, trackNumber, file, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/releases/${id}/tracks/${trackNumber}/instrumental-alternate`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: createUploadProgressHandler(onProgress),
    });
  },
  deleteTrackInstrumentalAlternate: (id, trackNumber) => api.delete(`/releases/${id}/tracks/${trackNumber}/instrumental-alternate`),
  getTrackAppleDigitalMaster: (id, trackNumber) => api.get(`/releases/${id}/tracks/${trackNumber}/apple-digital-master`),
  uploadTrackAppleDigitalMaster: (id, trackNumber, file, email, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    if (email) formData.append('email', email);
    return api.post(`/releases/${id}/tracks/${trackNumber}/apple-digital-master`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: createUploadProgressHandler(onProgress),
    });
  },
  deleteTrackAppleDigitalMaster: (id, trackNumber) => api.delete(`/releases/${id}/tracks/${trackNumber}/apple-digital-master`),
  getTrackStatus: (taskId) => api.get(`/track-status/${taskId}`),
};

// Dashboard
export const dashboardAPI = {
  getStats: (ownedOnly = true) => api.get('/dashboard/stats', { params: { owned_only: ownedOnly } }),
};

export const awardsAPI = {
  getMine: () => api.get('/artist-awards/me'),
  updateVisibility: (awardId, publicVisible) => api.patch(`/artist-awards/${awardId}/visibility`, { public_visible: publicVisible }),
};

export const audienceAPI = {
  getPreSaves: (releaseId) => api.get('/audience/pre-saves', { params: { release_id: releaseId } }),
  updatePreSave: (signupId, data) => api.patch(`/audience/pre-saves/${signupId}`, data),
};

// Bank
export const bankAPI = {
  getSummary: () => api.get('/bank/summary'),
  downloadMonthlyStatement: (period) => api.get('/bank/monthly-statement.csv', {
    params: period ? { period } : undefined,
    responseType: 'blob',
  }),
  savePayoutProfile: (data) => api.post('/bank/payout-profile', data),
  withdraw: (data) => api.post('/bank/withdraw', data),
  adjustEarnings: (data) => api.post('/bank/adjustments', data),
};

export const enterpriseAPI = {
  getCatalog: () => api.get('/enterprise/catalog'),
  importTooLostCatalog: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/enterprise/catalog/import-too-lost', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Team
export const teamAPI = {
  get: () => api.get('/team'),
  invite: (data) => api.post('/team/invite', data),
  updateMember: (id, data) => api.patch(`/team/members/${id}`, data),
  removeMember: (id) => api.delete(`/team/members/${id}`),
  updateInvite: (id, data) => api.patch(`/team/invites/${id}`, data),
  getInvite: (inviteId) => api.get(`/team/invites/${inviteId}`),
  acceptInvite: (inviteId) => api.post(`/team/invites/${inviteId}/accept`),
};

export const enterpriseArtistsAPI = {
  getAll: () => api.get('/enterprise/artists'),
  getSlotUsage: () => api.get('/enterprise/artists/slots'),
  create: (data) => api.post('/enterprise/artists', data),
  update: (id, data) => api.patch(`/enterprise/artists/${id}`, data),
  delete: (id) => api.delete(`/enterprise/artists/${id}`),
  importCsv: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/enterprise/artists/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
};

// Tickets
export const ticketsAPI = {
  create: (data) => api.post('/tickets', data),
  aiAssist: (data) => api.post('/tickets/ai-assist', data),
  getAll: () => api.get('/tickets'),
  getOne: (id) => api.get(`/tickets/${id}`),
  reply: (id, data) => api.post(`/tickets/${id}/reply`, { ...data, reply_as: 'customer' }),
};

// Tools
export const toolsAPI = {
  youtubeOacRequest: (channelId, topicLink, releaseId) => 
    api.post('/tools/youtube-oac-request', { channel_id: channelId, topic_link: topicLink, release_id: releaseId }),
  spotifyVerificationRequest: (spotifyUri, releaseId) => 
    api.post('/tools/spotify-verification-request', { spotify_uri: spotifyUri, release_id: releaseId }),
  createContentIdDispute: (data) => api.post('/tools/content-id-dispute', data),
  createContentIdApplication: (data) => api.post('/tools/content-id-applications', data),
  createContentIdAlbumApplication: (data) => api.post('/tools/content-id-album-applications', data),
  createContentIdApplicationWithAudio: (data) => api.post('/tools/content-id-applications-with-audio', data),
  createStandaloneContentIdApplication: (data) => api.post('/tools/content-id-standalone-applications', data),
  getContentIdApplications: () => api.get('/tools/content-id-applications'),
  rescanContentIdApplication: (id) => api.post(`/tools/content-id-applications/${id}/rescan`),
  deleteContentIdApplication: (id) => api.delete(`/tools/content-id-applications/${id}`),
  queryContentIdClaims: (data) => api.post('/tools/content-id/claims/query', data),
  releaseContentIdClaim: (data) => api.post('/tools/content-id/claims/release', data),
  fixerRequest: (data) => api.post('/tools/fixer-request', data),
  createSpecialAccessRequest: (data) => api.post('/tools/special-access-request', data),
  getLyrics: () => api.get('/tools/lyrics'),
  submitLyrics: (data) => api.post('/tools/lyrics', data),
  getVault: () => api.get('/tools/vault'),
  getAcrCloudScans: () => api.get('/tools/acrcloud-scans'),
  createAcrCloudScan: (file, notes = '', onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    if (notes) formData.append('notes', notes);
    return api.post('/tools/acrcloud-scans', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: createUploadProgressHandler(onProgress),
      timeout: 120000,
    });
  },
};

// Admin
export const adminAPI = {
  getBoardroom: () => api.get('/admin/boardroom'),
  getBoardroomDailyBriefing: (refresh = false) => api.get('/admin/boardroom/daily-briefing', { params: { refresh } }),
  getBoardroomDocumentCategories: () => api.get('/admin/boardroom/documents/categories'),
  getBoardroomDocuments: (params = {}) => api.get('/admin/boardroom/documents', { params }),
  createBoardroomDocument: (data) => api.post('/admin/boardroom/documents', data),
  updateBoardroomDocument: (id, data) => api.patch(`/admin/boardroom/documents/${id}`, data),
  submitBoardroomDocumentForReview: (id) => api.post(`/admin/boardroom/documents/${id}/submit-review`),
  reviewBoardroomDocument: (id, data) => api.post(`/admin/boardroom/documents/${id}/legal-review`, data),
  publishBoardroomDocument: (id) => api.post(`/admin/boardroom/documents/${id}/publish`),
  archiveBoardroomDocument: (id) => api.post(`/admin/boardroom/documents/${id}/archive`),
  downloadBoardroomDocumentUrl: (id, format = 'pdf') => `${API_URL}/admin/boardroom/documents/${id}/download?format=${encodeURIComponent(format)}`,
  getPresaves: () => api.get('/admin/presaves'),
  updatePresaveLinks: (id, data) => api.patch(`/admin/presaves/${id}`, data),
  fetchChromaprintCatalog: () => api.post('/admin/boardroom/chromaprint/fetch'),
  getChromaprintCatalog: (limit = 50000) => api.get('/admin/boardroom/chromaprint', { params: { limit } }),
  previewChromaprintImport: (csvFile, audioArchive) => {
    const formData = new FormData();
    formData.append('csv_file', csvFile);
    formData.append('audio_archive', audioArchive);
    return api.post('/admin/boardroom/chromaprint/import/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  importChromaprintLibrary: (csvFile, audioArchive) => {
    const formData = new FormData();
    formData.append('csv_file', csvFile);
    formData.append('audio_archive', audioArchive);
    return api.post('/admin/boardroom/chromaprint/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  previewBoardroomIsrcImport: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/admin/boardroom/isrc/import/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  importBoardroomIsrcCatalog: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/admin/boardroom/isrc/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  importSongMetadata: (file, applyUpdates = false) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('apply_updates', applyUpdates ? 'true' : 'false');
    return api.post('/admin/catalog/import-song-metadata', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  scanLiveCatalogReleases: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/admin/catalog/live-releases/scan', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  syncLiveCatalogReleaseStatuses: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/admin/catalog/live-releases/sync-statuses', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  syncLiveCatalogTakedowns: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/admin/catalog/live-releases/sync-takedowns', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  mergeLiveCatalogReleases: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/admin/catalog/live-releases/merge-matches', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  importLiveCatalogReleases: (data) => api.post('/admin/catalog/live-releases/import', data),
  linkLiveCatalogReleaseToExisting: (data) => api.post('/admin/catalog/live-releases/link-existing', data),
  getReleaseLibrary: () => api.get('/admin/releases/library'),
  getReleaseForEdit: (id) => api.get(`/admin/releases/${id}`),
  updateReleaseForEdit: (id, data) => api.patch(`/admin/releases/${id}`, data),
  updateReleaseStatus: (id, data) => api.patch(`/admin/releases/${id}/status`, data),
  updateReleaseCodes: (id, data) => api.patch(`/admin/releases/${id}/codes`, data),
  updateExportMetadata: (id, data) => api.patch(`/admin/releases/${id}/export-metadata`, data),
  getQueue: (distributionMode, options = {}) => api.get('/admin/queue', {
    params: {
      ...(distributionMode ? { distribution_mode: distributionMode } : {}),
      ...(options.sandbox ? { sandbox: true } : {}),
    },
  }),
  getNeedsAttention: () => api.get('/admin/needs-attention'),
  getVevoChannels: (status) => api.get('/admin/vevo-channels', { params: status ? { status } : undefined }),
  getVevoSignups: (status) => api.get('/admin/vevo-signups', { params: status ? { status } : undefined }),
  generateQueueAiSummary: (releaseId, forceRefresh = false) =>
    api.post(`/admin/releases/${releaseId}/ai-summary`, { force_refresh: !!forceRefresh }),
  askReleaseReviewAssistant: (releaseId, data) => api.post(`/admin/releases/${releaseId}/review-assistant`, data),
  askStaffHelper: (data) => api.post('/admin/staff-helper/chat', data),
  escalateStaffHelper: (data) => api.post('/admin/staff-helper/escalate', data),
  getStaffHelp: () => api.get('/admin/staff-help'),
  respondToStaffHelp: (id, data) => api.post(`/admin/staff-help/${id}/respond`, data),
  getRiskQueue: (minimumScore = 0) => api.get('/admin/risk-queue', { params: { minimum_score: minimumScore } }),
  updateRiskScore: (userId, data) => api.patch(`/admin/risk-queue/${userId}`, data),
  createRiskStrike: (userId, data) => api.post(`/admin/risk-queue/${userId}/strikes`, data),
  getAcrCloudScans: () => api.get('/admin/acrcloud-scans'),
  createAcrCloudScan: (file, notes = '', onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    if (notes) formData.append('notes', notes);
    return api.post('/admin/acrcloud-scans', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: createUploadProgressHandler(onProgress),
      timeout: 120000,
    });
  },
  generateCareerListing: (data) => api.post('/admin/careers/listings/generate', data),
  getTakedowns: () => api.get('/admin/takedowns'),
  getTakedownSummary: () => api.get('/admin/takedowns/summary'),
  getPublicTakedownRequests: () => api.get('/admin/public-takedown-requests'),
  updatePublicTakedownRequest: (id, status) => api.patch(`/admin/public-takedown-requests/${id}?status=${status}`),
  approveRelease: (id, data, force = false) => api.post(`/admin/releases/${id}/approve`, data, {
    params: force ? { force: true } : undefined,
  }),
  resubmitRelease: (id) => api.post(`/admin/releases/${id}/resubmit`),
  rescanReleaseModeration: (id) => api.post(`/admin/releases/${id}/rescan-moderation`),
  rescanAllQueueModeration: (distributionMode, options = {}) => api.post('/admin/queue/rescan-all', null, {
    params: {
      ...(distributionMode ? { distribution_mode: distributionMode } : {}),
      ...(options.sandbox ? { sandbox: true } : {}),
    },
  }),
  runReleaseChromaprintScan: (id) => api.post(`/admin/releases/${id}/chromaprint-scan`, null, { timeout: 120000 }),
  requestVerification: (id) => api.post(`/admin/releases/${id}/request-verification`),
  escalateRelease: (id, data) => api.post(`/admin/releases/${id}/escalate`, data),
  acceptVerification: (id) => api.post(`/admin/releases/${id}/accept-verification`),
  rejectRelease: (id, reason) => api.post(`/admin/releases/${id}/reject?reason=${encodeURIComponent(reason)}`),
  deleteRelease: (id) => api.delete(`/releases/${id}`),
  purgeRelease: (id) => api.delete(`/admin/releases/${id}/purge`),
  updateQueueSettings: (id, data) => api.patch(`/admin/releases/${id}/queue`, data),
  createSandboxRelease: (data) => api.post('/admin/sandbox/releases', data),
  getTooLostSandboxStatus: () => api.get('/admin/toolost/sandbox/status'),
  getTooLostSandboxAuthorizeUrl: (mode = 'authorize') => api.get('/admin/toolost/sandbox/authorize-url', { params: { mode } }),
  refreshTooLostSandboxToken: () => api.post('/admin/toolost/sandbox/refresh'),
  testTooLostSandboxMe: () => api.post('/admin/toolost/sandbox/test-me'),
  getTooLostSandboxExports: () => api.get('/admin/toolost/sandbox/exports'),
  exportTooLostSandboxRelease: (id) => api.post(`/admin/toolost/sandbox/releases/${id}/export`),
  getTooLostStatus: () => api.get('/admin/toolost/status'),
  getTooLostAuthorizeUrl: () => api.get('/admin/toolost/authorize-url'),
  refreshTooLostToken: () => api.post('/admin/toolost/refresh'),
  testTooLostMe: () => api.post('/admin/toolost/test-me'),
  exportTooLostRelease: (id, data = {}) => api.post(`/admin/toolost/releases/${id}/export`, data, { timeout: 900000 }),
  bulkExportTooLostReleases: (items) => api.post('/admin/toolost/bulk-export', { items }, { timeout: 1800000 }),
  refreshTooLostRelease: (id) => api.post(`/admin/toolost/releases/${id}/refresh`, null, { timeout: 120000 }),
  markStoreRejected: (id, data) => api.post(`/admin/releases/${id}/mark-store-rejected`, data),
  markStoreAccepted: (id) => api.post(`/admin/releases/${id}/mark-store-accepted`),
  updateVevoChannel: (id, data) => api.patch(`/admin/vevo-channels/${id}`, data),
  updateVevoSignup: (id, data) => api.patch(`/admin/vevo-signups/${id}`, data),
  getPartners: () => api.get('/admin/partners'),
  approvePartner: (id) => api.post(`/admin/partners/${id}/approve`),
  rejectPartner: (id) => api.post(`/admin/partners/${id}/reject`),
  getDeveloperProgram: () => api.get('/admin/developer'),
  createDeveloperApiKey: (data) => api.post('/admin/developer/keys', data),
  grantDeveloperApiKey: (id, data) => api.post(`/admin/developer/applications/${id}/grant-key`, data),
  rejectDeveloperApplication: (id, data = {}) => api.post(`/admin/developer/applications/${id}/reject`, data),
  revokeDeveloperApiKey: (id, data = {}) => api.post(`/admin/developer/keys/${id}/revoke`, data),
  getTickets: () => api.get('/admin/tickets'),
  getReceivingEmails: (params = {}) => api.get('/admin/receiving/emails', { params }),
  getReceivingEmail: (id) => api.get(`/admin/receiving/emails/${id}`),
  updateReceivingEmail: (id, data) => api.patch(`/admin/receiving/emails/${id}`, data),
  sendStaffMail: (data) => api.post('/admin/receiving/send', data),
  getStaffMailboxes: () => api.get('/admin/staff-mailboxes'),
  createStaffMailbox: (data) => api.post('/admin/staff-mailboxes', data),
  updateStaffMailbox: (id, data) => api.patch(`/admin/staff-mailboxes/${id}`, data),
  disableStaffMailbox: (id) => api.delete(`/admin/staff-mailboxes/${id}`),
  getTicketSummary: () => api.get('/admin/tickets/summary'),
  updateTicket: (id, data) => api.patch(`/admin/tickets/${id}`, data),
  generateTicketAiAssist: (id) => api.post(`/admin/tickets/${id}/ai-assist`),
  replyToTicket: (id, data) => api.post(`/tickets/${id}/reply`, { ...data, reply_as: 'staff' }),
  search: (query, limit = 6) => api.get('/admin/search', { params: { q: query, limit } }),
  getBankSummary: () => api.get('/admin/bank/summary'),
  completeWithdrawal: (id) => api.post(`/admin/withdrawals/${id}/complete`),
  updateWithdrawalStatus: (id, data) => api.patch(`/admin/withdrawals/${id}/status`, data),
  adjustEarnings: (data) => api.post('/bank/adjustments', data),
  resetArtistRoyalties: (data) => api.post('/admin/bank/reset-artist-royalties', data),
  getResetAllArtistRoyaltiesPreview: () => api.get('/admin/bank/reset-all-preview'),
  resetAllArtistRoyalties: (data) => api.post('/admin/bank/reset-all-artist-royalties', data),
  uploadBoardroomRoyaltyReport: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/admin/boardroom/royalties/import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getBoardroomRoyaltyReportSample: () => api.get('/admin/boardroom/royalties/sample', { responseType: 'blob' }),
  uploadRoyaltyReport: (file, artistMappings = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('artist_mappings', JSON.stringify(artistMappings));
    return api.post('/admin/upload-report', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getRoyaltyReportSample: () => api.get('/admin/upload-report/sample', { responseType: 'blob' }),
  downloadAssetsZipUrl: (id) => `${API_URL}/admin/releases/${id}/assets.zip`,
  exportPendingCount: () => api.get('/admin/export/pending-count'),
  getPendingSubmissions: () => api.get('/admin/export/pending'),
  getTooLostTestExport: () => api.get('/admin/export/toolost-test.csv', { responseType: 'blob' }),
  getDdexReleases: () => api.get('/admin/ddex/releases'),
  createDdexTestRelease: (data) => api.post('/admin/ddex/test-releases', data),
  createFullDdexTestRelease: (metadata, artworkFile, trackAudioFiles, onProgress) => {
    const formData = new FormData();
    formData.append('metadata', JSON.stringify(metadata));
    if (artworkFile) formData.append('artwork_file', artworkFile);
    trackAudioFiles.forEach((file, index) => {
      if (file) formData.append(`audio_file_${index + 1}`, file);
    });
    return api.post('/admin/ddex/test-releases/full', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: createUploadProgressHandler(onProgress),
    });
  },
  getDdexReadiness: (id) => api.get(`/admin/ddex/releases/${id}/readiness`),
  previewDdexPackage: (id) => api.post(`/admin/ddex/releases/${id}/preview`),
  downloadDdexXml: (id) => api.get(`/admin/ddex/releases/${id}/xml`, { responseType: 'blob' }),
  downloadDdexPackage: (id) => api.get(`/admin/ddex/releases/${id}/package.zip`, { responseType: 'blob' }),
  sendDdexToDummyStore: (id) => api.post(`/admin/ddex/releases/${id}/send-dummy-store`),
  getStoreArtists: () => api.get('/admin/store/artists'),
  updateStoreArtistBadge: (id, data) => api.patch(`/admin/store/artists/${id}/badge`, data),
  updateStoreAccountVerification: (id, data) => api.patch(`/admin/store/accounts/${id}/verification`, data),
  getStoreReleases: () => api.get('/admin/store/releases'),
  getStoreRelease: (id) => api.get(`/admin/store/releases/${id}`),
  syncStoreRelease: (id) => api.post(`/admin/store/releases/${id}/sync`),
  backfillStoreReleases: () => api.post('/admin/store/releases/backfill'),
  getStoreClaims: () => api.get('/admin/store/claims'),
  reviewStoreClaim: (id, data) => api.patch(`/admin/store/claims/${id}`, data),
  getInstantClaimDashboard: () => api.get('/store/instant-claim/dashboard'),
  createStoreClaim: (data) => api.post('/store/claims', data),
  recordStoreStreamEvent: (trackId, data) => api.post(`/store/tracks/${encodeURIComponent(trackId)}/stream-event`, data),
  getDummyStoreDeliveries: () => api.get('/dummy-store/deliveries'),
  getDummyStoreDelivery: (id) => api.get(`/dummy-store/deliveries/${id}`),
  markDummyStoreDeliveryReviewing: (id) => api.post(`/dummy-store/deliveries/${id}/reviewing`),
  acceptDummyStoreDelivery: (id) => api.post(`/dummy-store/deliveries/${id}/accept`),
  rejectDummyStoreDelivery: (id, reason) => api.post(`/dummy-store/deliveries/${id}/reject`, { reason }),
  markDistributed: (id, data) => api.post(`/admin/releases/${id}/mark-distributed`, data),
  rejectReleaseFromExport: (id, data) => api.post(`/admin/releases/${id}/reject-release`, data),
  // Special Access
  getSpecialAccessRequests: () => api.get('/admin/special-access'),
  getSpecialAccessResponseTemplates: () => api.get('/admin/special-access/templates'),
  updateSpecialAccessRequest: (id, data) => api.patch(`/admin/special-access/${id}`, data),
  // Staff Management
  getStaff: () => api.get('/admin/staff'),
  createStaff: (data) => api.post('/admin/staff', data),
  updateStaffPermissions: (id, data) => api.patch(`/admin/staff/${id}/permissions`, data),
  deleteStaff: (id) => api.delete(`/admin/staff/${id}`),
  getCareerListings: () => api.get('/admin/careers/listings'),
  createCareerListing: (data) => api.post('/admin/careers/listings', data),
  updateCareerListing: (id, data) => api.patch(`/admin/careers/listings/${id}`, data),
  deleteCareerListing: (id) => api.delete(`/admin/careers/listings/${id}`),
  getCareerApplications: () => api.get('/admin/careers/applications'),
  updateCareerApplication: (id, data) => api.patch(`/admin/careers/applications/${id}`, data),
  deleteCareerApplication: (id) => api.delete(`/admin/careers/applications/${id}`),
  bulkDeleteCareerApplications: (ids) => api.post('/admin/careers/applications/bulk-delete', { ids }),
  getModeration: (params) => api.get('/admin/moderation', { params }),
  moderateUser: (id, data) => api.post(`/admin/moderation/users/${id}`, data),
  removeModerationIP: (data) => api.post('/admin/moderation/ip-bans/remove', data),
  getDistroGridOverview: () => api.get('/distrogrid/admin/overview'),
  getDistroGridApplications: (params) => api.get('/distrogrid/admin/applications', { params }),
  decideDistroGridApplication: (id, data) => api.post(`/distrogrid/admin/applications/${id}/decision`, data),
  getDistroGridTenants: () => api.get('/distrogrid/admin/tenants'),
  createDistroGridTenant: (data) => api.post('/distrogrid/admin/tenants', data),
  updateDistroGridTenantStatus: (tenantId, data) => api.patch(`/distrogrid/admin/tenants/${tenantId}/status`, data),
  updateDistroGridTenantKyb: (tenantId, data) => api.patch(`/distrogrid/admin/tenants/${tenantId}/kyb`, data),
  getDistroGridReleases: (params) => api.get('/distrogrid/admin/releases', { params }),
  actionDistroGridRelease: (id, data) => api.post(`/distrogrid/admin/releases/${id}/action`, data),
  getDistroGridApiCustomers: () => api.get('/distrogrid/admin/api-customers'),
  createDistroGridApiKey: (tenantId, data) => api.post(`/distrogrid/admin/tenants/${tenantId}/keys`, data),
  revokeDistroGridApiKey: (keyId, data) => api.post(`/distrogrid/admin/api-keys/${keyId}/revoke`, data),
  setDistroGridTenantDomain: (tenantId, data) => api.put(`/distrogrid/admin/tenants/${tenantId}/domain`, data),
  setDistroGridTenantPortalSlug: (tenantId, data) => api.put(`/distrogrid/admin/tenants/${tenantId}/portal-slug`, data),
  getDistroGridPricing: () => api.get('/distrogrid/admin/pricing'),
  getDistroGridDomains: () => api.get('/distrogrid/admin/domains'),
  getDistroGridBilling: () => api.get('/distrogrid/admin/billing'),
  getDistroGridRoyalties: () => api.get('/distrogrid/admin/royalties'),
  getDistroGridAudit: (params) => api.get('/distrogrid/admin/audit', { params }),
  getDistroGridSystemHealth: () => api.get('/distrogrid/admin/system-health'),
  getUsers: () => api.get('/admin/users'),
  getUserCases: (id) => api.get(`/admin/users/${id}/cases`),
  createUserCase: (id, data) => api.post(`/admin/users/${id}/cases`, data),
  updateUserCase: (id, caseId, data) => api.patch(`/admin/users/${id}/cases/${caseId}`, data),
  getUserEmailOptions: (id) => api.get(`/admin/users/${id}/email-options`),
  sendUserEmail: (id, data) => api.post(`/admin/users/${id}/email`, data),
  getUserTosSigning: (id) => api.get(`/admin/users/${id}/tos-signing`),
  updateUserTosSigning: (id, data) => api.patch(`/admin/users/${id}/tos-signing`, data),
  impersonateUser: (id) => api.post(`/admin/users/${id}/impersonate`),
  resetUserMfa: (id) => api.post(`/admin/users/${id}/mfa/reset`),
  updateEnterpriseAccessProfile: (id, data) => api.patch(`/admin/enterprise-access/${id}`, data),
  uploadEnterpriseAccessImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/admin/enterprise-access/upload-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getDemoAccounts: () => api.get('/admin/demo/accounts'),
  createDemoAccount: (data) => api.post('/admin/demo/accounts', data),
  updateDemoAccount: (id, data) => api.patch(`/admin/demo/accounts/${id}`, data),
  deleteDemoAccount: (id) => api.delete(`/admin/demo/accounts/${id}`),
  cancelUserSubscription: (id, data = {}) => api.post(`/admin/users/${id}/cancel-subscription`, data),
  grantTrialOffer: (id, data = {}) => api.post(`/admin/users/${id}/trial-offer`, data),
  revokeTrialOffer: (id, data = {}) => api.post(`/admin/users/${id}/trial-offer/revoke`, data),
  sendTrialReminder: (id) => api.post(`/admin/users/${id}/send-trial-reminder`),
  getComplianceQueue: () => api.get('/admin/compliance'),
  getComplianceEnforcement: () => api.get('/admin/compliance/enforcement'),
  getComplianceEvidencePack: (userId) => api.get(`/admin/compliance/accounts/${userId}/evidence-pack`),
  updateRightsAppeal: (id, data) => api.patch(`/admin/compliance/rights-appeals/${id}`, data),
  removeRiskStrike: (id, data = {}) => api.delete(`/admin/compliance/strikes/${id}`, { data }),
  getComplianceDocument: (userId) => api.get(`/admin/compliance/${userId}/document`, { responseType: 'blob' }),
  getOwnerVaultDocument: (userId) => api.get(`/admin/owner-vault/${userId}/document`, { responseType: 'blob' }),
  verifyCompliance: (userId) => api.post(`/admin/compliance/${userId}/verify`),
  getOwnerVault: () => api.get('/admin/owner-vault'),
  getOwnerVaultAccess: () => api.get('/admin/owner-vault/access'),
  getOwnerVaultFileAccess: () => api.get('/admin/owner-vault/file-access'),
  unlockOwnerVault: (data) => api.post('/admin/owner-vault/unlock', data),
  setOwnerVaultPassword: (data) => api.post('/admin/owner-vault/password', data),
  unlockOwnerVaultFiles: (data) => api.post('/admin/owner-vault/file-unlock', data),
  lockOwnerVaultFiles: () => api.post('/admin/owner-vault/file-lock'),
  setOwnerVaultFilePassword: (data) => api.post('/admin/owner-vault/file-password', data),
  deleteOwnerVaultDocument: (userId) => api.delete(`/admin/owner-vault/${userId}/document`),
  getMarketingAudienceSummary: () => api.get('/admin/marketing/audience-summary'),
  sendMarketingBroadcast: (data) => api.post('/admin/marketing/broadcast', data),
  getCalendar: () => api.get('/admin/calendar'),
  updateCalendarSettings: (data) => api.put('/admin/calendar/settings', data),
  startCalendarOAuth: () => api.post('/admin/calendar/oauth/start'),
  disconnectCalendar: () => api.delete('/admin/calendar/connection'),
  getArtistAwards: () => api.get('/admin/artist-awards'),
  uploadArtistAwardAsset: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/admin/artist-awards/upload-asset', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  createArtistAward: (data) => api.post('/admin/artist-awards', data),
  updateArtistAward: (id, data) => api.patch(`/admin/artist-awards/${id}`, data),
  sendArtistAward: (id) => api.post(`/admin/artist-awards/${id}/send`),
  scanFirstReleaseAwards: () => api.post('/admin/artist-awards/scan-first-release'),
  getAffiliates: () => api.get('/admin/affiliates'),
  createAffiliate: (data) => api.post('/admin/affiliates', data),
  updateAffiliate: (id, data) => api.patch(`/admin/affiliates/${id}`, data),
  deleteAffiliate: (id) => api.delete(`/admin/affiliates/${id}`),
  syncAffiliateStripe: (id) => api.post(`/admin/affiliates/${id}/sync-stripe`),
  uploadAffiliateImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/admin/affiliates/upload-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getNewsPosts: () => api.get('/admin/news'),
  createNewsPost: (data) => api.post('/admin/news', data),
  updateNewsPost: (id, data) => api.patch(`/admin/news/${id}`, data),
  deleteNewsPost: (id) => api.delete(`/admin/news/${id}`),
  getMafa: () => api.get('/admin/mfeb'),
  updateMafaApplication: (id, data) => api.patch(`/admin/mfeb/applications/${id}`, data),
  createMafaContent: (kind, data) => api.post(`/admin/mfeb/${kind}`, data),
  updateMafaContent: (kind, id, data) => api.patch(`/admin/mfeb/${kind}/${id}`, data),
  deleteMafaContent: (kind, id) => api.delete(`/admin/mfeb/${kind}/${id}`),
  createMafaNews: (data) => api.post('/admin/mfeb-news', data),
  updateMafaNews: (id, data) => api.patch(`/admin/mfeb-news/${id}`, data),
  uploadMafaImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/admin/mfeb/upload-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getRoadmapItems: () => api.get('/admin/roadmap'),
  createRoadmapItem: (data) => api.post('/admin/roadmap', data),
  updateRoadmapItem: (id, data) => api.patch(`/admin/roadmap/${id}`, data),
  deleteRoadmapItem: (id) => api.delete(`/admin/roadmap/${id}`),
  getLegalPages: () => api.get('/admin/legal-pages'),
  updateLegalPage: (slug, data) => api.patch(`/admin/legal-pages/${slug}`, data),
  getCompanyPolicyCategories: () => api.get('/admin/company-policies/categories'),
  getCompanyPolicies: () => api.get('/admin/company-policies'),
  createCompanyPolicy: (data) => api.post('/admin/company-policies', data),
  updateCompanyPolicy: (id, data) => api.patch(`/admin/company-policies/${id}`, data),
  publishCompanyPolicy: (id) => api.post(`/admin/company-policies/${id}/publish`),
  archiveCompanyPolicy: (id) => api.post(`/admin/company-policies/${id}/archive`),
  acknowledgeCompanyPolicy: (id, version) => api.post(`/admin/company-policies/${id}/acknowledge`, { version }),
  getCompanyPolicyAcknowledgments: (slug) => api.get('/admin/company-policies/acknowledgments', { params: slug ? { policy_slug: slug } : {} }),
  getSiteStaff: () => api.get('/admin/site-staff'),
  createSiteStaff: (data) => api.post('/admin/site-staff', data),
  updateSiteStaff: (id, data) => api.patch(`/admin/site-staff/${id}`, data),
  deleteSiteStaff: (id) => api.delete(`/admin/site-staff/${id}`),
  getAccountManagers: () => api.get('/admin/account-managers'),
  createAccountManager: (data) => api.post('/admin/account-managers', data),
  updateAccountManager: (id, data) => api.patch(`/admin/account-managers/${id}`, data),
  deleteAccountManager: (id) => api.delete(`/admin/account-managers/${id}`),
  uploadAccountManagerImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/admin/account-managers/upload-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getYoutubeResolutionCenter: () => api.get('/admin/youtube-resolution'),
  getYoutubeReleaseOptions: () => api.get('/admin/youtube-resolution/releases'),
  createManualContentIdImport: (data) => api.post('/admin/youtube-resolution/manual-import', data),
  previewSourceAudioImport: (caseId) => api.get(`/admin/youtube-resolution/${caseId}/sourceaudio-preview`),
  syncSourceAudioClaimsForCase: (caseId, data = {}) => api.post(`/admin/youtube-resolution/${caseId}/sourceaudio-claims/sync`, data),
  bulkSourceAudioImport: (data) => api.post('/admin/youtube-resolution/bulk-sourceaudio-import', data, { timeout: 300000 }),
  createYoutubeWhitelistRequest: (data) => api.post('/admin/youtube-resolution/whitelist', data),
  createYoutubeDisputeRequest: (data) => api.post('/admin/youtube-resolution/disputes', data),
  generateYoutubeReleaseOfClaim: (caseId) => api.post(`/admin/youtube-resolution/${caseId}/release-of-claim`),
  resolveYoutubeResolutionCase: (caseId, data) => api.post(`/admin/youtube-resolution/${caseId}/resolve`, data),
  rescanYoutubeResolutionCase: (caseId) => api.post(`/admin/youtube-resolution/${caseId}/rescan`),
  bulkRescanYoutubeResolutionCases: (caseIds) => api.post('/admin/youtube-resolution/bulk-rescan', { case_ids: caseIds }),
  bulkRejectYoutubeResolutionCases: (caseIds, notes) => api.post('/admin/youtube-resolution/bulk-reject', { case_ids: caseIds, notes }),
  deleteYoutubeResolutionCase: (caseId) => api.delete(`/admin/youtube-resolution/${caseId}`),
  querySourceAudioWhitelist: (data) => api.post('/admin/sourceaudio/whitelist/query', data),
  addSourceAudioWhitelist: (data) => api.post('/admin/sourceaudio/whitelist', data),
  removeSourceAudioWhitelist: (data) => api.post('/admin/sourceaudio/whitelist/remove', data),
  querySourceAudioClaims: (data) => api.post('/admin/sourceaudio/claims/query', data),
  releaseSourceAudioClaims: (data) => api.post('/admin/sourceaudio/claims/release', data),
  uploadSiteStaffImage: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/admin/site-staff/upload-image', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getSiteAnnouncement: () => api.get('/admin/site-announcement'),
  updateSiteAnnouncement: (data) => api.patch('/admin/site-announcement', data),
  getSiteUpdateNotice: () => api.get('/admin/site-update-notice'),
  updateSiteUpdateNotice: (data) => api.patch('/admin/site-update-notice', data),
  getStatusComponents: () => api.get('/admin/status-components'),
  updateStatusComponents: (data) => api.patch('/admin/status-components', data),
  getSocialModerationQueue: (status = 'pending_review', params = {}) => api.get('/admin/social/moderation-queue', { params: { status, ...params } }),
  getSocialPosts: (params = {}) => api.get('/admin/social/posts', { params }),
  reviewSocialPost: (id, data) => api.patch(`/admin/social/posts/${id}/review`, data),
  reviewSocialComment: (id, data) => api.patch(`/admin/social/comments/${id}/review`, data),
  getSocialReports: (status = 'open') => api.get('/admin/social/reports', { params: { status } }),
  updateSocialReport: (id, data) => api.patch(`/admin/social/reports/${id}`, data),
  getSocialAppeals: (status = 'pending') => api.get('/admin/social/appeals', { params: { status } }),
  updateSocialAppeal: (id, data) => api.patch(`/admin/social/appeals/${id}`, data),
  getSocialDmcaNotices: (status = 'open') => api.get('/admin/social/dmca-notices', { params: { status } }),
  updateSocialDmcaNotice: (id, data) => api.patch(`/admin/social/dmca-notices/${id}`, data),
  getSocialAutoModerationSettings: () => api.get('/admin/social/auto-moderation/settings'),
  updateSocialAutoModerationSettings: (data) => api.patch('/admin/social/auto-moderation/settings', data),
  simulateSocialModeration: (data) => api.post('/admin/social/auto-moderation/simulate', data),
  getSocialAutoModerationEvents: (params = {}) => api.get('/admin/social/auto-moderation/events', { params }),
  getSocialBlockedKeywords: (params = {}) => api.get('/admin/social/blocked-keywords', { params }),
  createSocialBlockedKeyword: (data) => api.post('/admin/social/blocked-keywords', data),
  updateSocialBlockedKeyword: (id, data) => api.patch(`/admin/social/blocked-keywords/${id}`, data),
  deleteSocialBlockedKeyword: (id) => api.delete(`/admin/social/blocked-keywords/${id}`),
  getSocialUsers: (params = {}) => api.get('/admin/social/users', { params }),
  getSocialAffiliations: () => api.get('/admin/social/affiliations'),
  updateSocialAffiliation: (id, data) => api.patch(`/admin/social/users/${id}/affiliations`, data),
  getSocialAnalytics: (params = {}) => api.get('/admin/social/analytics', { params }),
  updateSocialUserRestriction: (id, data) => api.patch(`/admin/social/users/${id}/restriction`, data),
  getVerificationApplications: (status = 'pending') => api.get('/admin/verification/applications', { params: { status } }),
  reviewVerificationApplication: (id, data) => api.patch(`/admin/verification/applications/${id}`, data),
  updateVerificationEligibility: (id, data) => api.patch(`/admin/users/${id}/verification-eligibility`, data),
  updateVerificationStatus: (id, data) => api.patch(`/admin/users/${id}/verification-status`, data),
  updateUser: (id, data) => api.patch(`/admin/users/${id}`, data),
  banUser: (id, data) => api.post(`/admin/users/${id}/ban`, data),
  initiateManualReview: (id, data) => api.post(`/admin/users/${id}/manual-review`, data),
  clearManualReview: (id) => api.post(`/admin/users/${id}/manual-review/clear`),
  forceAgeCheck: (id) => api.post(`/admin/users/${id}/force-age-check`),
  updateAgeVerificationStatus: (id, data) => api.post(`/admin/users/${id}/age-verification-status`, data),
  unbanUser: (id) => api.post(`/admin/users/${id}/unban`),
  deleteUser: (id, data = {}) => api.delete(`/admin/users/${id}`, { data }),
  getLyrics: (status) => api.get('/admin/lyrics', { params: { status } }),
  updateLyrics: (id, data) => api.patch(`/admin/lyrics/${id}`, data),
  getHelpArticles: () => api.get('/admin/help-articles'),
  createHelpArticle: (data) => api.post('/admin/help-articles', data),
  updateHelpArticle: (id, data) => api.patch(`/admin/help-articles/${id}`, data),
};
